import { useCallback, useMemo, useState } from "react";
import { v6 as uuidv6 } from "uuid";
import {
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { MenuItem, Select, Stack, TextField } from "@mui/material";

const OPERATORS = [
  { value: "=", label: "=" },
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
];

const NumericFilter = (props: GridRenderHeaderFilterProps) => {
  const { colDef } = props;
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const currentFilter = useMemo(
    () => filterModel.items?.find(({ field }) => field === colDef.field),
    [colDef.field, filterModel.items]
  );

  const [localOperator, setLocalOperator] = useState(
    currentFilter?.operator || "="
  );
  const value = currentFilter?.value ?? "";

  const updateFilter = useCallback(
    (newOperator: string, newValue: string) => {
      if (newValue === "") {
        if (currentFilter) {
          apiRef.current?.deleteFilterItem(currentFilter);
        }
        return;
      }
      apiRef.current?.upsertFilterItems([
        {
          ...(currentFilter || { id: uuidv6(), field: colDef.field }),
          operator: newOperator,
          value: newValue,
        },
      ]);
    },
    [apiRef, colDef.field, currentFilter]
  );

  return (
    <Stack direction="row" spacing={0.5} sx={{ width: "100%", px: 0.5 }}>
      <Select
        size="small"
        value={localOperator}
        onChange={(e) => {
          const newOp = e.target.value;
          setLocalOperator(newOp);
          if (value !== "") {
            updateFilter(newOp, value);
          }
        }}
        sx={{ minWidth: 48, "& .MuiSelect-select": { py: "6px" } }}
      >
        {OPERATORS.map((op) => (
          <MenuItem key={op.value} value={op.value}>
            {op.label}
          </MenuItem>
        ))}
      </Select>
      <TextField
        size="small"
        type="number"
        value={value}
        onChange={(e) => updateFilter(localOperator, e.target.value)}
        sx={{ flex: 1 }}
        slotProps={{ htmlInput: { "data-testid": "numeric-filter-input" } }}
      />
    </Stack>
  );
};

export default NumericFilter;
